import { api } from '../../lib/apiClient';

export const tenantKeys = {
  organizations: () => ['organizations'],
  workspaces: (orgId) => ['workspaces', orgId ?? 'none'],
  members: (orgId) => ['members', orgId ?? 'none'],
  invitations: (orgId) => ['invitations', orgId ?? 'none'],
  memberWorkspaces: (orgId, memberId) => ['member-workspaces', orgId ?? 'none', memberId ?? 'none'],
  myInvitations: () => ['my-invitations'],
  // imageKey is part of the key so a replaced image lands in a fresh cache entry.
  // Prefix-matchable on workspaceId alone to drop every blob for one workspace.
  workspaceImage: (workspaceId, imageKey) => [
    'workspace-image',
    workspaceId ?? 'none',
    imageKey ?? 'none',
  ],
};

// ---- Organizations / workspaces ----
export async function getOrganizations() {
  const { data } = await api.get('/organizations');
  return data;
}

export async function updateOrganization({ orgId, name }) {
  const { data } = await api.patch(`/organizations/${orgId}`, { name });
  return data;
}

export async function getWorkspaces(orgId) {
  const { data } = await api.get(`/organizations/${orgId}/workspaces`);
  return data;
}

export async function getWorkspaceCapabilities({ orgId, workspaceId }) {
  const { data } = await api.get(
    `/organizations/${orgId}/workspaces/${workspaceId}/capabilities`,
  );
  return data;
}

export async function createWorkspace({ orgId, name }) {
  const { data } = await api.post(`/organizations/${orgId}/workspaces`, { name });
  return data;
}

export async function updateWorkspace({ orgId, workspaceId, name }) {
  const { data } = await api.patch(`/organizations/${orgId}/workspaces/${workspaceId}`, { name });
  return data;
}

export const deleteWorkspace = ({ orgId, workspaceId }) =>
  api.delete(`/organizations/${orgId}/workspaces/${workspaceId}`);

export async function uploadWorkspaceImage({ orgId, workspaceId, file }) {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.put(`/organizations/${orgId}/workspaces/${workspaceId}/image`, form);
  return data;
}

export const deleteWorkspaceImage = ({ orgId, workspaceId }) =>
  api.delete(`/organizations/${orgId}/workspaces/${workspaceId}/image`);

// Image bytes need the bearer header, so they arrive as a blob (no plain <img src>).
// `imageKey` rides along as `?v=` purely to bust the *browser* HTTP cache: the route
// is a fixed URL served with `Cache-Control: private, max-age=300`, so without it a
// replaced image keeps returning the old bytes for five minutes even though React
// Query re-fetches. Every upload mints a fresh key, so the URL changes with the image.
// The server reads only its path params and ignores the extra query param.
export async function getWorkspaceImageBlob({ orgId, workspaceId, imageKey }) {
  const { data } = await api.get(`/organizations/${orgId}/workspaces/${workspaceId}/image`, {
    responseType: 'blob',
    params: imageKey ? { v: imageKey } : undefined,
  });
  return data;
}

// ---- Members ----
export async function getMembers(orgId) {
  const { data } = await api.get(`/organizations/${orgId}/members`);
  return data;
}

export async function updateMemberRole({ orgId, memberId, role }) {
  const { data } = await api.patch(`/organizations/${orgId}/members/${memberId}`, { role });
  return data;
}

export const removeMember = ({ orgId, memberId }) =>
  api.delete(`/organizations/${orgId}/members/${memberId}`);

// ---- Member workspace assignments (edit access after acceptance) ----
export async function getMemberWorkspaces({ orgId, memberId }) {
  const { data } = await api.get(`/organizations/${orgId}/members/${memberId}/workspaces`);
  return data; // [{ workspaceId, name, role, permissions }]
}

export async function setMemberWorkspaces({ orgId, memberId, assignments }) {
  const { data } = await api.put(`/organizations/${orgId}/members/${memberId}/workspaces`, {
    assignments,
  });
  return data;
}

// ---- Invitations ----
export async function getInvitationCapacity(orgId) {
  const { data } = await api.get(`/organizations/${orgId}/invitations/capacity`);
  return data;
}

export async function getInvitations(orgId) {
  const { data } = await api.get(`/organizations/${orgId}/invitations`);
  return data;
}

/** workspaces: [{ workspaceId, role, permissions? }]. */
export async function createInvitation({ orgId, email, workspaces }) {
  const { data } = await api.post(`/organizations/${orgId}/invitations`, {
    email,
    workspaces,
  });
  return data; // { invitation, emailSent, userExists }
}

export const revokeInvitation = ({ orgId, invitationId }) =>
  api.delete(`/organizations/${orgId}/invitations/${invitationId}`);

/** Pending invitations addressed to the current user (existing-user accept flow). */
export async function getMyInvitations() {
  const { data } = await api.get('/invitations/mine');
  return data;
}

/** Public, no-secret preview for the accept landing page. */
export async function previewInvitation(token) {
  const { data } = await api.get('/invitations/preview', { params: { token } });
  return data; // { email, orgName, inviterName, workspaces, userExists }
}

export async function acceptInvitation(token) {
  const { data } = await api.post('/invitations/accept', { token });
  return data;
}

/** Accept one of my pending invitations by id, in-app (no raw token needed). */
export async function acceptMyInvitation(invitationId) {
  const { data } = await api.post(`/invitations/mine/${invitationId}/accept`);
  return data;
}

/** New-user acceptance: set a password to create the account and join. */
export async function acceptInvitationAsNewUser({ token, displayName, password }) {
  const { data } = await api.post('/auth/accept-invitation', { token, displayName, password });
  return data; // AuthResponse { accessToken, refreshToken, user }
}
